/**
 * 统一的初始节点加载模块
 * 用于在各种论坛模式中一致地加载和转换 timing="immediate" 的初始节点
 */

import { getInitialConfig, convertSeedNodesToDisplayNodes } from './activityInfoAccessors';

/**
 * 为不同的论坛模式加载初始节点
 * 
 * @param {Object} activityInfo - 活动信息对象
 * @param {string} mode - 论坛模式: 'sticker', 'chatroom', 'tagged-forum'
 * @param {Object} options - 可选配置
 * @param {string} options.groupId - 群组 ID（推荐从 sessionStorage 获取）
 * @param {boolean} options.prefixActivityDescription - 是否在节点前加入活动说明
 * @returns {Array} 转换后的初始节点数组
 */
export function loadInitialNodesForMode(activityInfo, mode, options = {}) {
  try {
    // 1. 提取 timing="immediate" 的种子节点
    const { nodes: seedNodes } = getInitialConfig({ data: activityInfo });
    
    if (!Array.isArray(seedNodes) || seedNodes.length === 0) {
      console.log(`[InitialNodesLoader] No immediate nodes found for ${mode} mode`);
      return [];
    }

    // 2. 准备转换选项
    const activityDescription = activityInfo?.Activity?.content || '';
    const groupId = options.groupId || sessionStorage.getItem('groupId');
    
    const convertOptions = {
      prefixActivityDescription: options.prefixActivityDescription || false,
      activityDescription,
      groupId, // 确保所有节点都有 groupId
    };

    // 3. 转换为标准节点格式（带负整数 ID）
    const convertedNodes = convertSeedNodesToDisplayNodes(seedNodes, convertOptions);

    // 4. 根据不同模式添加特定字段
    const nodesWithModeSpecificFields = convertedNodes.map(node => {
      const baseNode = { ...node };
      
      switch (mode) {
        case 'sticker':
          // ForumSticker: 需要 label 字段用于 ReactFlow 显示
          return {
            ...baseNode,
            label: node.title || ''
          };
          
        case 'chatroom':
          // ForumChatRoom: 需要特定格式的 label
          return {
            ...baseNode,
            label: `📝\n\n${node.title}\n\n主持人\n${new Date(node.createdAt).toLocaleString()}`
          };
          
        case 'tagged-forum':
          // TagBasedForum: 需要 rawData 和特定的 title/label 映射
          return {
            ...baseNode,
            label: node.title || '',
            title: node.content, // 使用 content 作为 title
            rawData: {
              id: node.id,
              title: node.title,
              content: node.content,
              author: node.author,
              tags: node.tags,
              createdAt: node.createdAt,
              groupId: node.groupId
            }
          };
          
        default:
          console.warn(`[InitialNodesLoader] Unknown mode: ${mode}, returning base node`);
          return baseNode;
      }
    });

    console.log(`[InitialNodesLoader] Loaded ${nodesWithModeSpecificFields.length} nodes for ${mode} mode`);
    return nodesWithModeSpecificFields;
    
  } catch (error) {
    console.error(`[InitialNodesLoader] Error loading initial nodes for ${mode}:`, error);
    return [];
  }
}

/**
 * 快捷方法：为 Sticker 模式加载初始节点
 */
export function loadInitialNodesForSticker(activityInfo, groupId) {
  return loadInitialNodesForMode(activityInfo, 'sticker', { groupId });
}

/**
 * 快捷方法：为 ChatRoom 模式加载初始节点
 */
export function loadInitialNodesForChatRoom(activityInfo, groupId) {
  return loadInitialNodesForMode(activityInfo, 'chatroom', { groupId });
}

/**
 * 快捷方法：为 TaggedForum 模式加载初始节点
 */
export function loadInitialNodesForTaggedForum(activityInfo, groupId, prefixActivityDescription = true) {
  return loadInitialNodesForMode(activityInfo, 'tagged-forum', {
    groupId,
    prefixActivityDescription
  });
}

/**
 * 验证初始节点是否有效
 * @param {Array} nodes - 节点数组
 * @returns {boolean} 是否所有节点都有效
 */
export function validateInitialNodes(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return true; // 空数组也是有效的
  }

  return nodes.every(node => {
    const hasValidId = typeof node.id === 'number' && Number.isInteger(node.id) && node.id < 0; // 必須是負整數，以符合 Edge.from/to 的 INTEGER 型態約束
    const hasTitle = typeof node.title === 'string';
    const hasContent = typeof node.content === 'string';
    const hasAuthor = typeof node.author === 'string';
    const hasTag = typeof node.tags === 'string' && node.tags.length > 0; // 必须有标签，但不限定具体值
    const hasCreatedAt = typeof node.createdAt === 'string';
    const hasGroupId = node.groupId !== undefined && node.groupId !== null;

    // 特别检查：所有种子节点都应标记 isInitial: true
    if (!node.isInitial) {
      console.warn('[InitialNodesLoader] Missing isInitial flag for seed node:', node.id);
    }

    if (!hasValidId) {
      console.error('[InitialNodesLoader] Invalid node ID:', node.id, 'Expected negative integer (to match Edge.from/to INTEGER type)');
    }
    if (!hasGroupId) {
      console.warn('[InitialNodesLoader] Missing groupId for node:', node.id);
    }

    return hasValidId && hasTitle && hasContent && hasAuthor && hasTag && hasCreatedAt && hasGroupId;
  });
}
